//—— 搖龜殼 ——
//
//  扣完券、卦已經定好之後的那一幕：滑動龜殼 → 三枚銅錢拋出來翻轉落定
//  → 從三枚的正反讀出一爻。六爻集滿才往下走。
//
//  畫面分左右：左邊是那一爻的三枚銅錢，右邊是讀出來的爻。
//  這個排法是為了讓因果看得見 —— 爻不是憑空出現的，是從那三枚讀出來的。
//
//  —— 他搖的是「揭露」不是「決定」 ——
//
//  六爻在後端扣券的那一刻就擲好了（見 routes/divination-route 的 D-3），
//  連每一枚銅錢的正反都存著（rows[].coins）。所以畫面上這三枚
//  **就是真的決定那一爻的那三枚**，不是另外隨機生出來的裝飾。
//
//  為什麼不讓前端真的擲：亂數在前端的話，重整頁面就能一直重搖到
//  喜歡的卦為止 —— 券扣了、卦卻能換；而且事後客訴「我搖的不是這個卦」
//  時沒有任何紀錄站得住。

import { useEffect, useRef, useState } from "react";

import useSound from "../../../../hooks/useSound";

import "./fuyaozi-shake-style.scss";

const BG = `${process.env.PUBLIC_URL}/images/fuyaozi/bg-fuyaozi-chat.jpg`;
const SHELL = `${process.env.PUBLIC_URL}/images/fuyaozi/turtle-shell.png`;

//—— 音效 ——
//  兩個都是 CC0（公眾領域）的素材，來源 bigsoundbank.com，
//  商用免授權費、免標示。原檔各十幾秒，已經剪成單次事件的長度。
//
//  只有一個：龜殼裡三枚銅錢互相碰撞（自己疊出來的，見下）。
//
//  —— 銅錢落地的那三聲拿掉了（Kevin 2026-10-08 決定）——
//  原本三枚各自落地時會再響一聲，但那變成「燈燈燈」三連響，
//  像在報分數而不是在卜卦。搖的那一下已經說完整件事了。
//
//  —— shake 不是直接剪一段，是疊出來的 ——
//  素材「45 枚硬幣落入陶罐」裡找出三次**乾淨的單枚撞擊**（前後都沒有
//  別的聲音蓋進來），再按「傾過去三枚先後撞上、回擺再撞一次、餘韻」
//  的節奏疊成八下。直接剪一段只會聽成「一枚」——
//  那一段本來就只是一次撞擊的衰減。
//
//  三個素材刻意取自不同時間點：同一個樣本疊三次聽起來像回音，不像三枚錢。
//
//  ⚠️ 最早用的是「手中搖骰子」，但那是**塑膠**的聲音 ——
//  量過撞擊後的衰減：骰子 2ms 就沒了，硬幣要 55ms 才衰到 10%。
//  金屬會響、塑膠是悶的，差 27 倍。
//
//  瀏覽器只允許「使用者互動之後」發聲，而這兩個的觸發點都在
//  滑動／點擊事件裡，所以不會被擋。
const SOUND_SHAKE = `${process.env.PUBLIC_URL}/sounds/shake.m4a`;

//銅錢的正反兩面。放在 public/ 而不是 import 進來：
//  角色素材是內容不是程式資產，換圖直接覆蓋檔案就好，不用重新編譯。
//  （也因為在 public/ 底下，所以不能在 scss 裡用 url()，要像這樣
//    以 inline style 給 —— 跟聊天室的頭像同一個處理。）
const COIN_FACE = {
  front: {
    backgroundImage: `url(${process.env.PUBLIC_URL}/images/fuyaozi/money-front.png)`,
  },
  back: {
    backgroundImage: `url(${process.env.PUBLIC_URL}/images/fuyaozi/money-back.png)`,
  },
};

//滑多遠才算「搖了一下」。
//  40px 是手指輕輕一撥的距離 —— 再小會被「只是碰到」誤觸發，
//  再大則要刻意用力甩，六次下來手會累。
const SWIPE_PX = 40;

//—— 一次搖卦的節奏（毫秒）——
//  這幾個數字要跟 scss 的 keyframes 對齊，改一邊就要改另一邊。
//
//    殼晃      銅錢拋出、翻轉、落定（三枚錯開）   爻浮現
//    520       900（每枚）＋ 錯開 110×2          400
const SHAKE_MS = 520;
const COIN_FLY_MS = 900;
const COIN_STAGGER_MS = 110;
//最後一枚落定的時刻（從銅錢開始算）
const COINS_SETTLED_MS = COIN_FLY_MS + COIN_STAGGER_MS * 2;
//爻比最後一枚銅錢再晚一點 —— 讓人看清楚是幾正幾反，才給答案
const YAO_DELAY_MS = COINS_SETTLED_MS + 180;
//整段走完之前不接受下一次搖，不然快速連滑會疊出好幾列動畫
const BUSY_MS = SHAKE_MS + YAO_DELAY_MS + 400;

//閒置多久才跳出手勢提示。
//
//  3 秒：短到真的卡住的人不會乾等，長到正常操作的人看不到它 ——
//  搖完一爻到下一次滑動之間本來就有一兩秒，太短會變成每搖一次就閃一下。
//
//  不是「只在第一次出現」：有人第一輪看懂了，第三輪分心去做別的事回來
//  又忘了；也有人根本沒注意到第一次那一下。以「他現在是不是卡住了」
//  判斷，比以「他是不是新手」判斷準。
const IDLE_MS = 3000;

//六爻集滿之後、交棒給下一幕之前留的一口氣。
//  最後一爻才剛浮出來就切走，等於沒給人看完整張卦。
const DONE_BEAT_MS = 1600;

//銅錢翻幾圈才停。
//  正面停在整圈（360 的倍數），背面多半圈 —— 這樣最後朝上的那一面
//  就是真正的結果，不必另外蓋一層遮罩去「喬對」。
//
//  五圈是抓的：再少看起來像翻個身就定了（不像拋的），
//  再多會轉到讓人失去耐心，而三枚各自錯開已經夠熱鬧了。
const SPINS = 5;
//  1 = 正面、0 = 背面（見 utils/wenwang-calculator 的擲錢說明）
const spinDeg = (face) => SPINS * 360 + (face === 1 ? 0 : 180);

//中文數字。只用到六，不值得為它引一個套件 ——
//  索引就是「第幾個」減一，所以 CN[0] 是「一」。
const CN = ["一", "二", "三", "四", "五", "六"];

//爻的名稱。動爻要標出來 —— 那是這一卦真正在動的地方。
const yaoLabel = (row) => {
  if (row.moving) return row.yang ? "老陽" : "老陰";
  return row.yang ? "少陽" : "少陰";
};

//  gua     後端回來的那一卦（buildGua 的結果，含 rows）。
//          **可能是 undefined** —— 這一幕在 API 還沒回來時就先進場了
//          （見 fuyaozi-ask-page 的 startDivination），那時候只有背景，
//          龜殼要等卦到了才浮出來。
//  onDone  六爻都揭露完
const FuyaoziShake = ({ gua, onDone }) => {
  //已經揭露幾爻。rows 是「由下往上」，所以這個數字同時是索引。
  const [revealed, setRevealed] = useState(0);
  const [shaking, setShaking] = useState(false);
  //整段動畫跑完之前都算忙碌，擋住下一次觸發
  const [busy, setBusy] = useState(false);
  //閒置超過 IDLE_MS 沒搖 → 跳手勢提示
  const [idle, setIdle] = useState(false);
  const [leaving, setLeaving] = useState(false);

  //手指按下的位置。用 ref 不用 state —— 它只在事件處理裡讀寫，
  //  每次移動都重畫一次畫面太浪費。
  const startRef = useRef(null);
  //這一次按下有沒有已經觸發過，避免一次滑動連跳好幾爻
  const firedRef = useRef(false);
  //離場只能發生一次
  const leftRef = useRef(false);
  const playShake = useSound(SOUND_SHAKE, { volume: 0.55 });

  const rows = gua?.rows || [];
  const total = rows.length;
  //卦到了沒。沒到的時候只有背景，龜殼與提示都不出現 ——
  //  擺一顆按不動的殼在那裡，比什麼都沒有更讓人困惑。
  const ready = total > 0;

  //⚠️ 一定要在 effect 裡重設。StrictMode 下是「掛載 → cleanup → 再掛載」，
  //  而 useRef 的初始值只在第一次掛載套用。
  useEffect(() => {
    leftRef.current = false;
    return () => {
      leftRef.current = true;
    };
  }, []);

  //—— 閒置偵測 ——
  //
  //  這支 effect 的依賴是 busy 與 revealed，所以「開始搖」與「搖完一爻」
  //  都會讓它重跑 —— 等於每次互動都把計時器歸零，不用另外寫重置。
  //
  //  動畫跑的時候不算閒置（而且那時候跳提示會跟落下的銅錢搶注意力）。
  useEffect(() => {
    //  還沒拿到卦時不算閒置 —— 那時候他不是卡住，是在等我們
    if (!ready || busy || revealed >= total) {
      setIdle(false);
      return undefined;
    }
    setIdle(false);
    const timer = setTimeout(() => setIdle(true), IDLE_MS);
    return () => clearTimeout(timer);
  }, [ready, busy, revealed, total]);

  //六爻集滿 → 留一口氣再交棒。
  //  要等最後一爻的銅錢翻完、爻也浮出來，所以把 YAO_DELAY_MS 加進去。
  useEffect(() => {
    if (!total || revealed < total) return undefined;
    const timer = setTimeout(() => {
      if (leftRef.current) return;
      leftRef.current = true;
      setLeaving(true);
      setTimeout(() => onDone?.(), 500);
    }, YAO_DELAY_MS + DONE_BEAT_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealed, total]);

  const shakeOnce = () => {
    if (busy || revealed >= total) return;
    setBusy(true);
    setShaking(true);

    playShake();

    //殼晃完才把銅錢倒出來 —— 同時發生看起來像兩件無關的事，
    //  晃完再出現才有「從殼裡掉出來」的因果。
    setTimeout(() => {
      setShaking(false);
      setRevealed((n) => Math.min(n + 1, total));
    }, SHAKE_MS);

    //銅錢翻完、爻也浮出來了才解除忙碌
    setTimeout(() => setBusy(false), BUSY_MS);
  };

  //—— 滑動偵測 ——
  //  用 pointer 事件而不是 touch：同一組程式碼在手指與滑鼠上都能用，
  //  桌機開發時不必另外接一套。
  const handleDown = (e) => {
    startRef.current = { x: e.clientX, y: e.clientY };
    firedRef.current = false;
    //  捕捉指標：手指滑出殼的範圍之後仍然收得到 move 與 up，
    //  不然滑快一點就會在中途斷掉、永遠觸發不了。
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handleMove = (e) => {
    if (!startRef.current || firedRef.current) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;
    //  不分方向：往哪邊撥都算搖。限定方向的話使用者要猜「要上下還是左右」，
    //  而真的搖龜殼本來就不是單一方向的動作。
    if (Math.hypot(dx, dy) < SWIPE_PX) return;
    firedRef.current = true;
    shakeOnce();
  };

  const handleUp = () => {
    startRef.current = null;
  };

  return (
    <div
      className={`fuyaozi-shake${leaving ? " is-leaving" : ""}`}
      style={{ backgroundImage: `url(${BG})` }}
    >
      {/* —— 上方：已經揭露的爻 ——
          column-reverse：DOM 順序是「初爻先」（跟 rows 一致、也跟出現的
          順序一致），但畫出來是初爻在最底、往上長。
          六爻的卦圖本來就是由下往上讀，畫反了是硬錯。 */}
      <div className="fuyaozi-shake__board">
        <ol className="fuyaozi-shake__yaos">
          {rows.slice(0, revealed).map((row) => (
            <li
              className={`fuyaozi-shake__row${row.moving ? " is-moving" : ""}`}
              key={row.position}
            >
              {/* 左：那一爻的三枚銅錢。
                  coins 是後端擲出來的原始結果（1 正 0 反），
                  所以這三枚就是真的決定這一爻的那三枚。 */}
              <span className="fuyaozi-shake__coins">
                {row.coins.map((face, i) => (
                  <span
                    className="fuyaozi-shake__coin"
                    key={i}
                    style={{ "--delay": `${i * COIN_STAGGER_MS}ms` }}
                    aria-hidden="true"
                  >
                    <span
                      className="fuyaozi-shake__coin-inner"
                      style={{ "--spin": `${spinDeg(face)}deg` }}
                    >
                      <i
                        className="fuyaozi-shake__coin-face is-front"
                        style={COIN_FACE.front}
                      />
                      <i
                        className="fuyaozi-shake__coin-face is-back"
                        style={COIN_FACE.back}
                      />
                    </span>
                  </span>
                ))}
              </span>

              {/* 右：從那三枚讀出來的爻。比銅錢晚一點出現 ——
                  先看清楚幾正幾反，再給答案。

                  —— 這裡刻意什麼字都不寫 ——
                  世應、地支、六親都要先知道**完整的六爻**才算得出來
                  （世爻看八宮、六親看卦首的五行）。在第三爻就標出「世」，
                  等於告訴使用者我們早就把六次都擲完了 —— 儀式就破功了。
                  那些資料等斷卦時再一起給。 */}
              <span
                className="fuyaozi-shake__yao"
                style={{ "--yao-delay": `${YAO_DELAY_MS}ms` }}
              >
                {/* 陽爻一整條、陰爻斷成兩截。
                    陽爻用單一元素而不是「兩截貼在一起」—— 兩個各自有
                    圓角的方塊接在中間會看得出一道接縫。 */}
                <span
                  className={`fuyaozi-shake__bar${row.yang ? " is-yang" : " is-yin"}`}
                  aria-label={yaoLabel(row)}
                >
                  <i />
                  {!row.yang && <i />}
                </span>

                {/* 動爻的記號（六爻的傳統畫法）：
                      動陽（老陽，三枚全背）→ ○
                      動陰（老陰，三枚全正）→ ×

                    這個不算洩漏：三枚同面使用者自己從銅錢就看得出來，
                    不需要知道整卦（世應與六親才需要，所以那些不畫）。

                    靜爻也要保留這個位置 —— 空的也佔寬度，
                    不然有記號的那幾爻會把爻條往左推、六條對不齊。 */}
                <span className="fuyaozi-shake__mark" aria-hidden="true">
                  {row.moving ? (row.yang ? "○" : "×") : ""}
                </span>
              </span>
            </li>
          ))}
        </ol>

        {/* 進度：空心點代表還沒搖出來的爻。
            有這排點就不必寫「還要幾次」—— 一眼看得出總共六次、
            現在到哪裡。文字只補一句總數，給第一次用的人。 */}
        <div className="fuyaozi-shake__progress" aria-hidden="true">
          {rows.map((row, i) => (
            <i
              key={row.position}
              className={i < revealed ? "is-done" : undefined}
            />
          ))}
        </div>

        {/* 當前進度放大、用金色；「共六爻」留灰當註解 ——
            兩段同一個灰階的話，壓在背景圖上整句都會糊掉。 */}
        <p className="fuyaozi-shake__count">
          {revealed >= total ? (
            <strong>六爻已成</strong>
          ) : (
            <>
              <strong>第 {CN[revealed] || revealed + 1} 爻</strong>
              <span>共 {CN[total - 1] || total} 爻</span>
            </>
          )}
        </p>
      </div>

      {/* —— 下方：龜殼 —— */}
      <div className="fuyaozi-shake__stage">
        <p className="fuyaozi-shake__hint">
          {!ready
            ? "伏爻子正在備卦"
            : revealed >= total
              ? "卦已成形"
              : revealed === 0
                ? `以指滑動龜殼，連搖${CN[total - 1] || total}次`
                : "以指滑動龜殼"}
        </p>

        <div className="fuyaozi-shake__shell-wrap">
          <button
            type="button"
            className={`fuyaozi-shake__shell${ready ? " is-ready" : ""}${
              shaking ? " is-shaking" : ""
            }`}
            style={{ backgroundImage: `url(${SHELL})` }}
            onPointerDown={handleDown}
            onPointerMove={handleMove}
            onPointerUp={handleUp}
            onPointerCancel={handleUp}
            //  鍵盤與輔助技術的退路：滑動本身沒辦法用鍵盤做，
            //  所以按 Enter／空白鍵也算搖一次。
            onClick={shakeOnce}
            disabled={!ready || busy || revealed >= total}
            aria-label={`搖龜殼，已成 ${revealed} 爻，共 ${total} 爻`}
          />

          {/* —— 手勢提示 ——
              手指左右來回，兩側箭頭跟著節奏亮 —— 靜態圖示只說得出
              「這裡可以碰」，要演出來他才知道是「滑」不是「點」。

              **三秒沒動作才出現**，一搖就收起來（見上面的閒置偵測）。
              不是「只教第一次」：有人第一輪看懂了，第三輪分心回來又忘了；
              也有人根本沒注意到第一次那一下。以「他現在是不是卡住了」
              判斷，比以「他是不是新手」判斷準。

              整層 pointer-events: none —— 它蓋在龜殼上，不關的話
              手指按在提示上反而滑不動，那就本末倒置了。 */}
          {idle && (
            <div className="fuyaozi-shake__gesture" aria-hidden="true">
              <svg
                className="fuyaozi-shake__arrow is-left"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  d="M15 5l-7 7 7 7"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>

              {/* 指尖朝上的手。用實心而不是線稿：線稿在這個尺寸
                  （36px）上筆畫會糊成一團，認不出是手。 */}
              <svg
                className="fuyaozi-shake__finger"
                viewBox="0 0 24 24"
                width="36"
                height="36"
              >
                <path
                  d="M10 4a1.6 1.6 0 0 1 3.2 0v7.1h.6a1.6 1.6 0 0 1 1.6 1.6v.6l2.5.9a2.1 2.1 0 0 1 1.3 2.4l-.7 3.2A3.1 3.1 0 0 1 15.4 22h-3.6a3.1 3.1 0 0 1-2.4-1.1l-3.2-3.9a1.7 1.7 0 0 1 2.3-2.4L10 16.2V4z"
                  fill="currentColor"
                />
              </svg>

              <svg
                className="fuyaozi-shake__arrow is-right"
                viewBox="0 0 24 24"
                width="20"
                height="20"
              >
                <path
                  d="M9 5l7 7-7 7"
                  stroke="currentColor"
                  strokeWidth="2.4"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FuyaoziShake;
